<script setup lang="ts">
import {ref} from "vue";
import {Component, Rule} from './rule-types'
import {useDialogs} from '@hantera/portal-app'
import {DropdownButton, DropdownItem} from '@hantera/design-system'
import RuleComponent from "./RuleComponent.vue";
import useBackend from "@/composition/useBackend";
import {v7 as uuid} from 'uuid';

const props = defineProps<{
  components: Component[]
  rule?: Rule
}>();

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'cancelled'): void
  (e: 'deleted'): void
}>()

const backend = useBackend()
const dialogs = useDialogs()

const displayName = ref<string>(props.rule?.displayName ?? '');
const activeFrom = ref(toBindableDate(props.rule?.activeFrom));
const activeTo = ref(toBindableDate(props.rule?.activeTo));
const ruleComponents = ref<{
  componentId: string
  parameters: Record<string, string>
}[]>(props.rule?.components ?? []);

function toBindableDate(date?: Date | string)
{
  if (!date) {
    return '';
  }
  
  if (typeof(date) === 'string') {
    date = new Date(date);
  }
  
  if (date.getFullYear() > 2099) {
    return '';
  }

  if (date.getFullYear() < 200) {
    return '';
  }
  
  const pad = (num: number) => String(Math.floor(Math.abs(num))).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}


function toUtc(date?: Date | string)
{
  if (!date) return null;
  
  if (typeof date === 'string')
  {
    date = new Date(date);
  }
  
  return date.toISOString();
}

async function save() {
  
  // TODO Add loading state (disable form)
  // TODO Validate form input
  
  const response = await backend.fetch(
    '/core/resources/rules/' + (props.rule?.ruleId ?? `promotion-periods/${uuid()}`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        displayName: displayName.value,
        activeFrom: toUtc(activeFrom.value),
        activeTo: toUtc(activeTo.value),
        components: ruleComponents.value.map(c => ({
          componentId: c.componentId,
          parameters: c.parameters
        }))
      })
    });
  
  if (!response.ok) {
    // TODO Display error
    return
  }
  
  emit('saved');
  
}

function addComponent(component: Component) {
  ruleComponents.value.push({
    componentId: component.componentId,
    parameters: {}
  })
}

function removeComponent(idx: number) {
  ruleComponents.value.splice(idx, 1)
}

async function deletePeriod() {
  if (await dialogs.confirm(
    `Instead of deleting, it's often better to deactivate.`,
    'Are you sure?',
    undefined,
    undefined,
    true
  ))
  {
    const response = await backend.fetch('/core/resources/rules/' + props.rule?.ruleId,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

    if (!response.ok) {
      // TODO Display error
      return
    }
    
    emit('deleted');
  }
}

</script>

<template>
  <form @submit.prevent.stop="save">
    <label>Name</label>
    <input type="text" v-model="displayName"/>

    <label>Active</label>
    <div class="active-inputs">
      <input type="datetime-local" v-model="activeFrom"/> -
      <input type="datetime-local" v-model="activeTo"/>
    </div>

    <label>Promotions</label>
    <div class="components">
      <RuleComponent
        v-for="(ruleComponent, idx) in ruleComponents"
        :key="idx"
        :rule-component="ruleComponent"
        :components="components"
        @remove="removeComponent(idx)"
      />
      <div>
        <DropdownButton label="Add" type="button">
          <DropdownItem
            v-for="component in components"
            :key="component.componentId"
            @click="addComponent(component)"
          >
            <b>{{component.displayName}}</b><br>
            {{component.description}}
          </DropdownItem>
        </DropdownButton>
      </div>
    </div>
    
    <div class="actions">
      <button class="btn-danger" type="button" @click="deletePeriod" v-if="rule">Delete</button>
      <div v-else></div>
      <div class="actions-buttons">
        <button class="btn-link" type="button" @click="emit('cancelled')">Cancel</button>
        <input type="submit" class="btn-primary" value="Save" />
      </div>
    </div>
  </form>
       
</template>

<style scoped>

form {
  max-width: 400px;
  display: block;
}

.active-inputs {
  display: grid;
  grid-template-columns: auto min-content auto;
  gap: 5px;
  width: 100%;
}

.components {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid hsl(var(--color-70));
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.actions-buttons {
  display: flex;
  gap: 10px;
}

.btn-danger {
  justify-self: flex-end;
}

</style>